import React from "react";
import { useFeatureFlag } from "../../hooks";
import { Routes } from "~/routes";
import { useRouter } from "next/navigation";
import { FeatureNames } from "../../flags";
import { OverlayHighlight } from "./OverlayHighlight";
import { RWA_COLORS } from "~/styles/colors";
import { useDebug } from "~/modules/debug/hooks/use-debug";

// // // //

/**
 * FeatureFlag
 * A component to feature-gate another component passed in via props.children
 * @param props.feature - the name of the Feature Flag. If props.feature is undefined this component will just pass-thru props.children
 * @param props.redirect - redirect the user here if the flag evaluates as false (optional)
 * @param props.children - the component being gated
 */
export function FeatureFlag(props: {
    feature: FeatureNames | undefined;
    redirect?: Routes;
    children: React.ReactNode;
    invert?: boolean;
}) {
    const { feature, redirect, children, invert = false } = props;

    const router = useRouter();

    // Check the debug.debug_preview_features redux state
    // We use this below to render props.children inside an OverlayHighlight component
    const { debug_preview_features } = useDebug();

    // Pass-thru props.children if feature is undefined
    if (feature === undefined) {
        return <React.Fragment>{children}</React.Fragment>;
    }

    // Evaluate the feature flag
    // eslint-disable-next-line
    const enabled = useFeatureFlag(feature);

    // Hide if enabled with invert prop
    if (enabled && invert === true) {
        return null;
    }

    // Render props.children if the flag evaluates as "true"
    if (enabled && invert === false) {
        // If the feature flag is not the experimental features flag, then just pass-thru the children
        if (
            feature !== FeatureNames.enableExperimentalFeatures ||
            debug_preview_features === false
        ) {
            return <React.Fragment>{children}</React.Fragment>;
        }

        return (
            <OverlayHighlight
                label="Feature Flagged - Not in GA"
                color={RWA_COLORS.red[400]}
            >
                {children}
            </OverlayHighlight>
        );
    }

    if (!enabled && invert === true) {
        // If the feature flag is not the experimental features flag, then just pass-thru the children
        if (
            feature !== FeatureNames.enableExperimentalFeatures ||
            debug_preview_features === false
        ) {
            return <React.Fragment>{children}</React.Fragment>;
        }

        return (
            <OverlayHighlight
                label="Currently Live in GA"
                color={RWA_COLORS.blue[400]}
            >
                {children}
            </OverlayHighlight>
        );
    }

    // Redirect the user if props.redirect is defined
    if (redirect) {
        const isClient: boolean = typeof window !== "undefined";
        if (isClient) {
            router.push(redirect);
        }
    }

    // Return null
    return null;
}
